import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { useTranslation } from '@/i18n'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const PAGE_SIZES = [10, 20, 50, 100]


type PaginatorProps = {
  currentPage: number
  totalPages: number
  totalItems: number
  pageSize: number
  setPage: (page: number) => void
  setPageSize: (size: number) => void
}

const Paginator = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  setPage,
  setPageSize,
}: PaginatorProps) => {
  const { t } = useTranslation()

  const handlePageSizeChange = (value: string) => {
    const newPageSize = Number(value)
    setPageSize(newPageSize)
    
    const currentPosition = (currentPage - 1) * pageSize
    const newTotalPages = Math.ceil(totalItems / newPageSize)
    const newPage = Math.max(1, Math.min(
      Math.floor(currentPosition / newPageSize) + 1,
      newTotalPages
    ))
    
    setPage(newPage)
  }

  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    } else if (currentPage <= 3) {
      return [1, 2, 3, 4, '...', totalPages]
    } else if (currentPage >= totalPages - 2) {
      return [
        1,
        '...',
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ]
    } else {
      return [
        1,
        '...',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        '...',
        totalPages,
      ]
    }
  }

  return (
    <div className="flex justify-between items-center p-4 border-t">
      {/* Page size selector */}
      <div className="flex items-center gap-2">
        <span>{t('common:pagination.show')}</span>
        <Select
          value={String(pageSize)}
          onValueChange={handlePageSizeChange}
        >
          <SelectTrigger className="w-20">
            <SelectValue placeholder={t('common:pagination.select_placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZES.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Pagination */}
      <Pagination>
        <PaginationContent>
          {/* Previous button */}
          {currentPage > 1 && (
            <PaginationItem>
              <PaginationPrevious onClick={() => setPage(currentPage - 1)} />
            </PaginationItem>
          )}

          {/* Page numbers */}
          {getPageNumbers().map((page, index) => (
            <PaginationItem key={index}>
              {typeof page === 'number' ? (
                <PaginationLink
                  isActive={page === currentPage}
                  onClick={() => setPage(page)}
                >
                  {page}
                </PaginationLink>
              ) : (
                <PaginationEllipsis />
              )}
            </PaginationItem>
          ))}

          {/* Next button */}
          {currentPage < totalPages && (
            <PaginationItem>
              <PaginationNext onClick={() => setPage(currentPage + 1)} />
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </div>
  )
}

export default Paginator
